import React, {useEffect} from "react";
import {connect} from "react-redux";
import * as PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { history } from "../../redux/store/configureStore";
import SchoolClassesList from "./SchoolClassesList";
import * as schoolClassActions from "../../redux/actions/schoolClassActions";
import * as teacherActions from "../../redux/actions/teacherActions";
import {filterArray, getById, minutesToTimeString, timeStringToMinutes} from "../common/Helpers";
import {CLASS_TYPES, WEEKDAYS} from "../common/Const";
import SchoolClassesFilterForm from "./SchoolClassesFilterForm";
import * as settingsActions from "../../redux/actions/settingsActions";
import { buildSchoolYearRange, getDefaultSchoolClassesFilters } from "../common/schoolYear";
import { buildManageRouteWithSchoolYear } from "./manageSchoolYearRouting";
import { getEffectivePermissionRole } from "./rbac";

const matchesDateRange = (schoolClass, fromDate, tillDate) => {
  const classFrom = schoolClass.fromDate;
  const classTill = schoolClass.tillDate || schoolClass.fromDate;

  if (fromDate && classTill < fromDate) {
    return false;
  }

  if (tillDate && classFrom > tillDate) {
    return false;
  }

  return true;
};

export function SchoolClasses({
  formattedClasses,
  teachers,
  loadSchoolClasses,
  loadTeachers,
  filters,
  updateFilters,
  activeSchoolYearStartYear,
  userRole,
}) {
  const location = useLocation();
  const activeSchoolYearRange = buildSchoolYearRange(activeSchoolYearStartYear);
  const constrainedFromDate = (
    filters.fromDate && filters.fromDate > activeSchoolYearRange.fromDate
      ? filters.fromDate
      : activeSchoolYearRange.fromDate
  );
  const constrainedTillDate = (
    filters.tillDate && filters.tillDate < activeSchoolYearRange.tillDate
      ? filters.tillDate
      : activeSchoolYearRange.tillDate
  );

  useEffect(() => {
    if (filters.fromDate !== constrainedFromDate || filters.tillDate !== constrainedTillDate) {
      updateFilters({
        ...filters,
        fromDate: constrainedFromDate,
        tillDate: constrainedTillDate,
      });
    }
  }, [constrainedFromDate, constrainedTillDate, filters, updateFilters]);

  useEffect(() => {
    loadSchoolClasses(constrainedFromDate, constrainedTillDate)
      .catch((error) => {
        alert(`Loading schoolClasses failed ${error}`);
      });
  }, [constrainedFromDate, constrainedTillDate, loadSchoolClasses]);

  useEffect(() => {
    if (teachers.length === 0) {
      loadTeachers()
        .catch((error) => {
          alert(`Loading teachers failed ${error}`);
        });
    }
  }, [teachers.length, loadTeachers]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    const newFilters = { ...filters, [name]: value };
    updateFilters(newFilters);
  };
  
  const handleBooleanFilterChange = (filterName) => {
    const newFilters = { ...filters, [filterName]: !filters[filterName] };
    updateFilters(newFilters);
  };
  
  const clearFilters = () => {
    updateFilters(getDefaultSchoolClassesFilters(new Date(), activeSchoolYearRange));
  };
  
  const filteredClasses = filterArray(filters.search, formattedClasses).filter((schoolClass) => {
    const startTime = schoolClass.startTime ? timeStringToMinutes(schoolClass.startTime) : null;
    const classType = getById(CLASS_TYPES, schoolClass.classTypeId);
    const endTime = classType && startTime ? startTime + classType.length : null;
  
    return (
      matchesDateRange(schoolClass, constrainedFromDate, constrainedTillDate) &&
      // Filter by isRecurring
      ((!filters.showOnlyRepeating && !filters.showOnlyNotRepeating) ||
        (filters.showOnlyRepeating !== filters.showOnlyNotRepeating && (
          (filters.showOnlyRepeating && schoolClass.isRecurring) ||
          (filters.showOnlyNotRepeating && !schoolClass.isRecurring)
        ))) &&
      // Filter by dayOfWeek
      (filters.dayOfWeek === '' || schoolClass.dayOfWeek === filters.dayOfWeek) &&
      // Filter by classTypeId
      (filters.classTypeId === '' || schoolClass.classTypeId === filters.classTypeId) &&
      // Filter by teacherId
      (filters.teacherId === '' || schoolClass.teacherId === filters.teacherId) &&
      // // Filter by startTime
      (filters.startTime === '' || (startTime && timeStringToMinutes(filters.startTime) <= startTime)) &&
      // // Filter by endTime
      (filters.endTime === '' || (endTime && timeStringToMinutes(filters.endTime) >= endTime)) &&
      // Filter by comment
      (!filters.comment || schoolClass.comment)
    );
  });


  const handleEditClass = (schoolClass) => {
    history.push(buildManageRouteWithSchoolYear(`/manage/class/${schoolClass.id}`, location.search, activeSchoolYearStartYear));
  };

  const handleAddNewClass = () => {
    history.push(buildManageRouteWithSchoolYear("/manage/class/new", location.search, activeSchoolYearStartYear));
  };

  return (
    <div className="mb-5">
      <h2 className="manage-title">Zajęcia</h2>
      <div className="row d-flex align-items-center mx-0">
        <input name="search" className="form-control form-group col-8" value={filters.search} onChange={handleFilterChange} placeholder="szukaj"/>

        <div className="col-4 col-md-2 col-xl-1 form-group justify-content-center d-flex">
          <SchoolClassesFilterForm
            filters={filters}
            handleFilterChange={handleFilterChange}
            clearFilters={clearFilters}
            handleBooleanFilterChange={handleBooleanFilterChange}
            teachers={teachers}
            weekdays={WEEKDAYS}
            classTypes={CLASS_TYPES}
          />
        </div>
        {userRole !== "TEACHER" && (
        <button
          type="button"
          className="btn manage-button btn-success col-md-2 col-xl-3 btn-block form-group"
          onClick={handleAddNewClass}
        >
          Nowe zajęcia
        </button>
        )}
      </div>

      <SchoolClassesList
        schoolClasses={filteredClasses}
        onAdd={handleAddNewClass}
        onEdit={handleEditClass}
      />
    </div>
  );
}

SchoolClasses.propTypes = {
  loadSchoolClasses: PropTypes.func.isRequired,
  teachers: PropTypes.array.isRequired,
  loadTeachers: PropTypes.func.isRequired,
  formattedClasses: PropTypes.array.isRequired,
  filters: PropTypes.object.isRequired,
  updateFilters: PropTypes.func.isRequired,
  activeSchoolYearStartYear: PropTypes.number.isRequired,
  userRole: PropTypes.string,
};

SchoolClasses.defaultProps = {
  userRole: "",
};

function formatSchoolClass(schoolClass, teachers) {
  const classType = getById(CLASS_TYPES, schoolClass.classTypeId);
  const dayOfWeek = getById(WEEKDAYS, schoolClass.dayOfWeek);
  const teacher = getById(teachers, schoolClass.teacherId);
  const startTime = timeStringToMinutes(schoolClass.startTime);
  const endTime = classType ? startTime + classType.length : null;
  const when = dayOfWeek && startTime && endTime ? `${dayOfWeek.name} ${minutesToTimeString(startTime)}-${minutesToTimeString(endTime)}` : '';
  const dateRange = schoolClass.tillDate ? `${schoolClass.fromDate} do ${schoolClass.tillDate}` : schoolClass.fromDate;
  const teacherName = teacher ? `${teacher.firstName} ${teacher.lastName}` : '';
  return {...schoolClass, classType: classType.name, when, dateRange, teacher: teacherName};
}

const mapStateToProps = state => ({
  schoolClasses: state.schoolClasses,
  teachers: state.teachers,
  formattedClasses: state.schoolClasses.map(schClass => formatSchoolClass(schClass, state.teachers)),
  filters: state.settings.schoolClassesFilters,
  activeSchoolYearStartYear: state.settings.activeSchoolYearStartYear,
  userRole: getEffectivePermissionRole(state.user),
});

const mapDispatchToProps = dispatch => ({
  loadSchoolClasses: (fromDate, toDate) => dispatch(schoolClassActions.loadSchoolClasses(fromDate, toDate)),
  loadTeachers: () => dispatch(teacherActions.loadTeachers()),
  updateFilters: filters => dispatch(settingsActions.updateSchoolClassesFilters(filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SchoolClasses);
